import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, type SecurityEvent, type SecurityStats } from '../api/client';
import {
  MagnifyingGlass,
  CaretLeft,
  CaretRight,
  CaretDown,
} from '@phosphor-icons/react';

const PAGE_SIZE = 30;

const SEVERITY_COLORS: Record<string, string> = {
  high: 'bg-[rgba(248,113,113,0.12)] text-[var(--color-error)]',
  medium: 'bg-[rgba(251,191,36,0.12)] text-[var(--color-warning)]',
  low: 'bg-[rgba(96,165,250,0.12)] text-[var(--color-info)]',
};

const SEVERITY_FILTERS = [
  { label: 'All', value: '' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

export function Security() {
  const { currentWorkspaceId } = useAuth();
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [stats, setStats] = useState<SecurityStats | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [severityFilter, setSeverityFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const onSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(0);
    }, 300);
  }, []);

  useEffect(() => {
    if (!currentWorkspaceId) return;
    setLoading(true);
    api
      .getSecurityEvents(currentWorkspaceId, {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        severity: severityFilter || undefined,
        q: debouncedSearch || undefined,
      })
      .then((data) => {
        setEvents(data.events);
        setStats(data.stats);
        setTotal(data.total);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [currentWorkspaceId, severityFilter, debouncedSearch, page]);

  if (!currentWorkspaceId) {
    return <p className="text-text-secondary">No workspace found. Complete onboarding first.</p>;
  }

  if (error) {
    return (
      <div className="rounded-lg bg-[rgba(248,113,113,0.12)] p-4 text-sm text-[var(--color-error)]">{error}</div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Security</h1>
        <p className="mt-1 text-sm text-text-secondary">
          DLP detections and security events for your workspace.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          <div className="rounded-lg border border-border-dark bg-surface-1 p-4">
            <p className="text-2xl font-bold text-text-primary">{stats.total}</p>
            <p className="text-xs text-text-secondary">Total Detections</p>
          </div>
          <div className="rounded-lg border border-[rgba(248,113,113,0.2)] bg-[rgba(248,113,113,0.12)] p-4">
            <p className="text-2xl font-bold text-[var(--color-error)]">{stats.high}</p>
            <p className="text-xs text-[var(--color-error)]">High Severity</p>
          </div>
          <div className="rounded-lg border border-[rgba(251,191,36,0.2)] bg-[rgba(251,191,36,0.12)] p-4">
            <p className="text-2xl font-bold text-[var(--color-warning)]">{stats.medium}</p>
            <p className="text-xs text-[var(--color-warning)]">Medium Severity</p>
          </div>
          <div className="rounded-lg border border-[rgba(96,165,250,0.2)] bg-[rgba(96,165,250,0.12)] p-4">
            <p className="text-2xl font-bold text-[var(--color-info)]">{stats.low}</p>
            <p className="text-xs text-[var(--color-info)]">Low Severity</p>
          </div>
          <div className="rounded-lg border border-border-dark bg-surface-1 p-4">
            <p className="text-2xl font-bold text-text-primary">{stats.unique_patterns}</p>
            <p className="text-xs text-text-secondary">Unique Patterns</p>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        {SEVERITY_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => { setSeverityFilter(f.value); setPage(0); }}
            className="rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors border"
            style={{
              backgroundColor: severityFilter === f.value ? 'var(--accent-teal)' : 'transparent',
              color: severityFilter === f.value ? 'var(--surface-0)' : 'var(--text-secondary)',
              borderColor: severityFilter === f.value ? 'var(--accent-teal)' : 'var(--border)',
            }}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto relative">
          <MagnifyingGlass
            size={14}
            weight="duotone"
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search tool, actor, pattern..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="rounded-md border pl-8 pr-3 py-1.5 text-xs w-56 outline-none transition-colors focus:border-[var(--accent-teal)]"
            style={{
              backgroundColor: 'var(--surface-0)',
              borderColor: 'var(--border)',
              color: 'var(--text-primary)',
            }}
          />
        </div>
      </div>

      {/* Events table */}
      <div className="rounded-lg border border-border-dark bg-surface-1">
        {loading && events.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner-gradient h-6 w-6" />
          </div>
        ) : events.length === 0 ? (
          <div className="p-8 text-center text-text-secondary">
            <svg className="mx-auto h-12 w-12 text-accent-teal" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
            </svg>
            <p className="mt-2 font-medium">No detections found</p>
            <p className="text-sm">
              {severityFilter || debouncedSearch
                ? 'No events match the current filters.'
                : 'All tool calls have been clean.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border-dark bg-surface-0">
                  <th className="w-8 px-2 py-3" />
                  <th className="px-4 py-3 font-medium text-text-secondary">Time</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Tool</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Actor</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Patterns</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Severity</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Redactions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {events.map((e) => {
                  const severity = (e.metadata?.severity as string) || 'low';
                  const detected = (e.metadata?.detected as string[]) || [];
                  const redactionCount = (e.metadata?.redaction_count as number) || 0;
                  const isExpanded = expandedRowId === e.event_id;
                  return (
                    <ExpandableRow
                      key={e.event_id}
                      event={e}
                      severity={severity}
                      detected={detected}
                      redactionCount={redactionCount}
                      isExpanded={isExpanded}
                      onToggle={() => setExpandedRowId(isExpanded ? null : e.event_id)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between border-t border-border-dark px-4 py-3">
            <span className="text-xs text-text-tertiary">
              Page {page + 1} of {totalPages} ({total} events)
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-40"
                style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
              >
                <CaretLeft size={12} weight="bold" /> Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-40"
                style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
              >
                Next <CaretRight size={12} weight="bold" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ExpandableRow({
  event,
  severity,
  detected,
  redactionCount,
  isExpanded,
  onToggle,
}: {
  event: SecurityEvent;
  severity: string;
  detected: string[];
  redactionCount: number;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const redactions = (event.metadata?.redactions as Array<{ path?: string; type?: string; method?: string; masked_preview?: string }>) || [];
  const targetUrl = event.metadata?.target_url as string | undefined;

  return (
    <>
      <tr
        className="hover:bg-surface-2 cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-2 py-3 text-text-tertiary">
          <CaretDown
            size={14}
            weight="bold"
            className={`transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
          />
        </td>
        <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
          {new Date(event.timestamp).toLocaleString()}
        </td>
        <td className="px-4 py-3 font-medium text-text-primary">
          {event.tool_name}
        </td>
        <td className="px-4 py-3 text-text-secondary">{event.actor_id}</td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-1">
            {detected.map((p) => (
              <span key={p} className="rounded bg-surface-2 px-1.5 py-0.5 text-xs text-text-primary">
                {p}
              </span>
            ))}
          </div>
        </td>
        <td className="px-4 py-3">
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${SEVERITY_COLORS[severity] || 'bg-surface-2 text-text-primary'}`}>
            {severity}
          </span>
        </td>
        <td className="px-4 py-3 text-text-secondary">{redactionCount}</td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} className="bg-surface-0 px-6 py-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-text-tertiary mb-1">Actor</p>
                <p className="text-sm text-text-primary font-mono">{event.actor_id}</p>
              </div>
              {targetUrl && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-text-tertiary mb-1">Target URL</p>
                  <p className="text-sm text-text-primary font-mono truncate">{targetUrl}</p>
                </div>
              )}
              <div>
                <p className="text-[10px] uppercase tracking-wider text-text-tertiary mb-1">Findings</p>
                <p className="text-sm text-text-primary">{detected.length} pattern{detected.length !== 1 ? 's' : ''} detected</p>
              </div>
            </div>

            {detected.length > 0 && (
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-wider text-text-tertiary mb-1.5">Detected Patterns</p>
                <div className="flex flex-wrap gap-1.5">
                  {detected.map((p) => (
                    <span
                      key={p}
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${SEVERITY_COLORS[severity] || 'bg-surface-2 text-text-primary'}`}
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {redactions.length > 0 && (
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-wider text-text-tertiary mb-1.5">Redaction Details</p>
                <div className="space-y-1.5">
                  {redactions.map((r, i) => (
                    <div key={i} className="rounded-md border border-border-dark bg-surface-1 px-3 py-2 text-xs">
                      <div className="flex flex-wrap gap-4">
                        {r.path && (
                          <span><span className="text-text-tertiary">Path:</span> <span className="text-text-primary font-mono">{r.path}</span></span>
                        )}
                        {r.type && (
                          <span><span className="text-text-tertiary">Type:</span> <span className="text-text-primary">{r.type}</span></span>
                        )}
                        {r.method && (
                          <span><span className="text-text-tertiary">Method:</span> <span className="text-text-primary">{r.method}</span></span>
                        )}
                      </div>
                      {r.masked_preview && (
                        <p className="mt-1 font-mono text-text-secondary">{r.masked_preview}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
